// Put-away (WP6, spec §4.2.4) in the design language v1 (mockup receiving-v1 §5): scan the
// pallet or container, read the proposed storage bin, scan the bin. The PUT_AWAY flow runs
// in the core interpreter; the host resolves the scan against the ledger (the container
// already exists), asks the server for the proposal and sends the Moved event. Another bin
// than the proposed one is allowed (enforcement *free*) and recorded in the event.

import { PUT_AWAY, applyInput, identityKey, parseScan, startRun, type Effect, type GS1Elements, type RunState, type ScanInput, classOf, uuidv7, type EventEnvelope } from "@trail/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Api, effectiveSettings as getSettings, type ContainerSummary, type PutAwayProposal, type PutAwayQueueItem } from "../api/client.js";
import { CameraView } from "../CameraView.js";
import { fmtQty, t } from "../i18n.js";
import { buildEmittedBatch } from "../receive/events.js";
import { pending, startQueueWorker, submitStep, subscribe, type QueuedStep } from "../receive/queue.js";
import { missingLocationMessage } from "../receive/location-message.js";
import { storageMismatchAny, buildRegistry, cacheBundle, loadCachedBundle, plainElements, holdsOf, palletCapacityOf, type Registry, findLocation } from "../receive/registry.js";
import { mergeTarget } from "../move/Move.js";
import { isGs1Symbology, type CodeState } from "../scan/camera.js";
import { feedback, unlockAudio } from "../scan/feedback.js";
import { attachWedge } from "../scan/wedge.js";
import { Actions, Banner, Btn, Chips, LabelStrip, ListItem, Prompt, Rows, ScanBar, Strip, focusScan, type BannerData, type LabelCell, type RowData } from "../ui/components.jsx";
import { reasonsForOverride } from "../receive/reasons.js";
import { BayElevation } from "../ui/map.jsx";
import { GotoScene } from "../ui/GotoScene.jsx";

const FLOW = PUT_AWAY;
const SCHEME = "gtin+lot" as const;
const shortSscc = (sscc: string) => `${sscc.slice(0, 4)}…${sscc.slice(-4)}`;

interface Unit {
  container: ContainerSummary;
  elements: GS1Elements;
}

export function PutAway({ onClose }: { onClose: () => void }) {
  const settings = useMemo(getSettings, []);
  const [registry, setRegistry] = useState<Registry | null>(() => {
    const cached = loadCachedBundle();
    return cached ? buildRegistry(cached) : null;
  });
  const [queue, setQueue] = useState<QueuedStep[]>(pending);
  const [waiting, setWaiting] = useState<PutAwayQueueItem[] | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [seq, setSeq] = useState(0);
  const [carry, setCarry] = useState<BannerData | null>(null);

  useEffect(() => {
    startQueueWorker();
    Api.bundle()
      .then((b) => {
        cacheBundle(b);
        setRegistry(buildRegistry(b));
      })
      .catch(() => undefined);
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const unsub = subscribe(setQueue);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      unsub();
    };
  }, []);

  useEffect(() => {
    Api.putAwayQueue().then(setWaiting).catch(() => setWaiting(null));
  }, [seq, queue.length]);

  const net = { online, queued: queue.length, stuck: queue.filter((q) => q.lastError && !/fetch|network|abort|5\d\d|401|429/i.test(q.lastError)).length };
  if (!registry) {
    return (
      <div className="screen">
        <Strip left={<b>{t("menu.putaway")}</b>} {...net} />
        <div className="body"><p className="hint">{t("common.loading")}</p></div>
        <Actions><Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn></Actions>
      </div>
    );
  }
  return (
    <Run
      key={seq}
      registry={registry}
      settings={settings}
      waiting={waiting}
      net={net}
      carry={carry}
      onDone={(banner) => {
        setCarry(banner);
        setSeq((s) => s + 1);
      }}
      onClose={onClose}
    />
  );
}

interface RunProps {
  registry: Registry;
  settings: ReturnType<typeof getSettings>;
  waiting: PutAwayQueueItem[] | null;
  net: { online: boolean; queued: number; stuck: number };
  carry: BannerData | null;
  onDone: (banner: BannerData) => void;
  onClose: () => void;
}

function Run({ registry, settings, waiting, net, carry, onDone, onClose }: RunProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [run, setRun] = useState<RunState>(() => startRun(FLOW).state);
  const runRef = useRef(run);
  runRef.current = run;
  const [unit, setUnit] = useState<Unit | null>(null);
  // What is on the pallet (owner 2026-11-02): a short list under the pallet, per article and lot.
  const [contents, setContents] = useState<ContainerSummary[] | null>(null);
  useEffect(() => {
    setContents(null);
    if (!unit || unit.container.kind !== "logistic-unit") return;
    let stop = false;
    Api.container(unit.container.container_id).then((c) => { if (!stop) setContents((c.children ?? []) as ContainerSummary[]); }).catch(() => { if (!stop) setContents([]); });
    return () => { stop = true; };
  }, [unit?.container.container_id]);
  const contentRows = useMemo((): RowData[] => {
    if (!contents || !registry) return [];
    const m = new Map<string, { name: string; lot: string | null; n: number; qty: number; uom: string }>();
    for (const c of contents) {
      const key = `${c.product_id ?? c.gtin ?? "?"}|${c.lot_number ?? ""}|${c.uom}`;
      const e = m.get(key) ?? { name: (c.product_id ? registry.bundle.products.find((p) => p.id === c.product_id)?.name : null) ?? `GTIN ${c.gtin ?? "?"}`, lot: c.lot_number, n: 0, qty: 0, uom: c.uom };
      e.n += 1; e.qty += Number(c.quantity) || 0;
      m.set(key, e);
    }
    return [...m.values()].map((e) => ({ k: e.name, small: e.lot ? `${t("adm.st.lot")} ${e.lot}` : undefined, q: `${e.n} × · ${fmtQty(String(Math.round(e.qty * 1000) / 1000), e.uom)}` }));
  }, [contents, registry]);
  const [proposal, setProposal] = useState<PutAwayProposal | null | "none">(null);
  const [banner, setBanner] = useState<BannerData | null>(carry);
  const [override, setOverride] = useState<{ state: RunState; destination: { id: string; code: string } } | null>(null);
  const [overrideReason, setOverrideReason] = useState<string | null>(null);
  const [overrideOther, setOverrideOther] = useState("");
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const scannedAt = useRef(new Date().toISOString());
  const locksRef = useRef<{ locationId: string; heldBy: string }[]>([]);
  useEffect(() => {
    const load = () => Api.countLocks().then((l) => { locksRef.current = l; }).catch(() => undefined);
    void load();
    const h = setInterval(() => void load(), 30_000);
    return () => clearInterval(h);
  }, []);

  const step = run.stepId ? FLOW.steps.find((s) => s.id === run.stepId) : null;

  const complete = useCallback(
    async (state: RunState, destination: { id: string; code: string }, reason: string | null = null) => {
      setBusy(true);
      // The reason rides on the Moved payload the flow emitted.
      const withReason = reason ? { ...state, emitted: state.emitted.map((em) => (em.event === "moved" ? { ...em, payload: { ...(em.payload as Record<string, unknown>), overrideReason: reason } } : em)) } : state;
      // Bulk stock joins the row of the same product already in the bin (owner 2026-11-03): Moved becomes Merged.
      const target = unit ? await mergeTarget(unit.container, destination.id) : null;
      const merged = target && unit ? { ...withReason, emitted: withReason.emitted.map((em) => (em.event === "moved" ? { event: "merged" as const, payload: { intoContainerId: target.container_id, fromContainerIds: (em.payload as { containerIds: string[] }).containerIds, fromLocationId: unit.container.location_id, toLocationId: destination.id, quantity: { value: String(Number(target.quantity) + Number(unit.container.quantity)), uom: unit.container.uom } } } : em)) } : withReason;
      const batch = buildEmittedBatch(merged, { settings, goodsOwnerId: registry.bundle.goodsOwner.id, readPoint: destination.id, flow: { id: FLOW.id, version: FLOW.version }, stepId: "move", captureDetail: target ? "merged" : null, occurredAt: scannedAt.current });
      let result: "stored" | "queued" = "queued";
      try {
        result = await submitStep(batch);
      } catch {
        result = "queued";
      }
      feedback("accepted");
      const joined = target ? ` ${t("move.merged", { q: fmtQty(target.quantity, target.uom), code: destination.code })}` : "";
      onDoneRef.current(result === "stored" ? { kind: "ok", label: t("putaway.moved", { code: destination.code }), message: (unit ? describeUnit(unit) : "") + joined } : { kind: "warn", label: t("putaway.movedQueued", { code: destination.code }), message: (unit ? describeUnit(unit) : "") + joined });
    },
    [settings, registry, unit],
  );

  const askProposal = useCallback(
    async (state: RunState, containerId: string) => {
      let value: { locationId: string | null; code: string | null } = { locationId: null, code: null };
      try {
        const r = await Api.putAwayPropose(containerId);
        if (r.proposal) {
          setProposal(r.proposal);
          value = { locationId: r.proposal.locationId, code: r.proposal.code };
        } else setProposal("none");
      } catch {
        setProposal("none");
      }
      const { state: next } = applyInput(FLOW, state, { kind: "answer", value });
      runRef.current = next;
      setRun(next);
    },
    [],
  );

  /** Resolve a scanned identity against the ledger: the container must already exist. */
  const resolveUnit = useCallback(async (elements: GS1Elements): Promise<ScanInput | null> => {
    const key = elements.sscc ? `sscc:00=${elements.sscc}` : identityKey(SCHEME, elements);
    if (!key) {
      setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.notFound") });
      return null;
    }
    let rows: ContainerSummary[];
    try {
      rows = await Api.containersByIdentity(key);
    } catch (e) {
      setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.lookupFailed", { err: e instanceof Error ? e.message : String(e) }) });
      return null;
    }
    if (rows.length === 0) {
      setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.notFound") });
      return null;
    }
    if (rows.length > 1) {
      setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.several", { n: rows.length }) });
      return null;
    }
    const c = rows[0]!;
    setUnit({ container: c, elements });
    return { kind: "scan", parsed: elements, resolved: { as: "container", containerId: c.container_id, containerKind: c.kind === "logistic-unit" ? "logistic-unit" : "trade-item", identityKey: c.identity_key } };
  }, []);

  const handleRaw = useCallback(
    async (data: string): Promise<CodeState> => {
      const before = runRef.current;
      const aim = /^\]\w\w/.exec(data)?.[0] ?? null;
      const text = aim ? data.slice(3) : data;
      let elements: GS1Elements | null = null;
      if (!(aim && !isGs1Symbology(data))) {
        try {
          elements = await parseScan(data);
        } catch {
          elements = null;
        }
      }
      if (before.stepId === "unit") {
        if (!elements || (!elements.sscc && !elements.gtin)) {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.notFound") });
          return "rejected";
        }
        setBusy(true);
        const input = await resolveUnit(elements);
        setBusy(false);
        if (!input) {
          feedback("rejected");
          return "rejected";
        }
        const { state, effects } = applyInput(FLOW, before, input);
        runRef.current = state;
        setRun(state);
        feedback("accepted");
        setBanner(null);
        scannedAt.current = new Date().toISOString();
        if (effects.some((e): e is Extract<Effect, { kind: "propose" }> => e.kind === "propose")) await askProposal(state, input.resolved.as === "container" ? input.resolved.containerId : "");
        return "accepted";
      }
      if (before.stepId === "destination" && elements?.sscc && unit) {
        // A pallet as the destination (owner 2026-11-02): the kolli goes onto it where it stands.
        if (unit.container.kind === "logistic-unit") {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.palletOnPallet") });
          return "rejected";
        }
        setBusy(true);
        let rows: ContainerSummary[] = [];
        try { rows = await Api.containersByIdentity(`sscc:00=${elements.sscc}`); } catch { rows = []; }
        setBusy(false);
        const p = rows.find((r) => r.kind === "logistic-unit") ?? null;
        if (!p) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.palletUnknown", { sscc: `${elements.sscc.slice(0, 4)}…${elements.sscc.slice(-4)}` }) }); return "rejected"; }
        if (p.state === "emptied" || p.state === "shipped") { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.palletRetired", { sscc: `${elements.sscc.slice(0, 4)}…${elements.sscc.slice(-4)}` }) }); return "rejected"; }
        if (unit.container.parent_id === p.container_id) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.onto.samePallet", { sscc: `${elements.sscc.slice(0, 4)}…${elements.sscc.slice(-4)}` }) }); return "rejected"; }
        setBusy(true);
        const batchId = uuidv7();
        const now = scannedAt.current;
        const mk = (type: EventEnvelope["type"], payload: Record<string, unknown>): EventEnvelope => ({ eventId: uuidv7(), tenantId: settings.tenantId, goodsOwnerId: registry.bundle.goodsOwner.id, type, class: classOf(type), schemaVersion: 1, occurredAt: now, actorId: settings.actorId, deviceId: settings.deviceId, capture: "barcode", captureDetail: null, readPoint: p.location_id, bizLocation: p.location_id, flowId: FLOW.id, flowVersion: FLOW.version, batchId, payload } as EventEnvelope);
        const events: EventEnvelope[] = [];
        if (unit.container.parent_id) events.push(mk("disaggregated", { parentContainerId: unit.container.parent_id, childContainerIds: [unit.container.container_id] }));
        if (p.location_id && unit.container.location_id !== p.location_id) events.push(mk("moved", { containerIds: [unit.container.container_id], fromLocationId: unit.container.location_id, toLocationId: p.location_id, proposedLocationId: null, overrideReason: null }));
        events.push(mk("aggregated", { parentContainerId: p.container_id, childContainerIds: [unit.container.container_id], completeness: "verified" }));
        let result: "stored" | "queued" = "queued";
        try { result = await submitStep({ flowId: FLOW.id, flowVersion: FLOW.version, stepId: "move", events, effects: [] }); } catch { result = "queued"; }
        setBusy(false);
        feedback("accepted");
        const code = p.location_id ? registry.bundle.locations.find((l) => l.id === p.location_id)?.code ?? "?" : "—";
        onDoneRef.current({ kind: result === "stored" ? "ok" : "warn", label: t("putaway.ontoPallet", { sscc: `${elements.sscc.slice(0, 4)}…${elements.sscc.slice(-4)}`, code }), message: describeUnit(unit) });
        return "accepted";
      }
      if (before.stepId === "destination") {
        const code = (elements?.ai["254"] ? registry.locationByGln.get(elements.ai["254"]) : findLocation(registry, text)) ?? null;
        if (!code) {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: missingLocationMessage(registry, text) ?? t("msg.unknownCode", { raw: text.slice(0, 40) }) });
          return "rejected";
        }
        // A location under count takes nothing until its counter is done (owner 2026-10-28); the server refuses too.
        const lock = locksRef.current.find((k) => k.locationId === code.id && k.heldBy !== settings.actorId);
        if (lock) {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("count.locked"), message: t("count.destinationLocked", { code: code.code, who: lock.heldBy }) });
          return "rejected";
        }
        // Temperature (owner 2026-10-31): a product needing another storage class than the place has.
        // A pallet is checked by its contents too (owner 2026-10-31: the check for pallets by what they carry).
        const mm = unit ? storageMismatchAny(registry, [unit.container.product_id, ...(contents ?? []).map((c) => c.product_id)], code.id) : null;
        if (mm?.action === "refuse") {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("storage.wrong"), message: t("storage.wrongMsg", { need: mm.need, has: mm.has, code: code.code }) });
          return "rejected";
        }
        if (mm) setBanner({ kind: "warn", label: t("storage.wrong"), message: t("storage.wrongMsg", { need: mm.need, has: mm.has, code: code.code }) });
        // What the place takes and how many pallets (owner 2026-11-02): a loose kolli cannot go into
        // pallet racking (use Flytta → Ny pall), and a full compartment takes no further pallet.
        if (unit) {
          const holds = holdsOf(registry, code.id);
          const capacity = palletCapacityOf(registry, code.id);
          if (unit.container.kind !== "logistic-unit" && holds === "pallets") {
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.needsPallet", { code: code.code }) });
            return "rejected";
          }
          if (unit.container.kind === "logistic-unit" && (holds === "loose" || capacity > 0)) {
            let pallets: ContainerSummary[] = [];
            try { pallets = (await Api.locationContents(code.id)).filter((c) => c.kind === "logistic-unit" && c.container_id !== unit.container.container_id); } catch { pallets = []; }
            if (holds === "loose") {
              feedback("rejected");
              setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.noPalletsHere", { code: code.code }) });
              return "rejected";
            }
            if (capacity > 0 && pallets.length >= capacity) {
              feedback("rejected");
              setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.full", { code: code.code, n: pallets.length, cap: capacity }) });
              return "rejected";
            }
          }
        }
        const input: ScanInput = { kind: "scan", parsed: elements ?? plainElements(text, aim), resolved: { as: "location", locationId: code.id, roles: code.roles } };
        const { state, effects } = applyInput(FLOW, before, input);
        const fb = effects.find((e): e is Extract<Effect, { kind: "feedback" }> => e.kind === "feedback");
        if (fb?.result !== "accepted") {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.notStorage", { code: code.code }) });
          return "rejected";
        }
        runRef.current = state;
        setRun(state);
        if (proposal && proposal !== "none" && proposal.locationId !== code.id) {
          feedback("attention");
          setBanner({ kind: "warn", label: t("banner.acceptedDeviation"), message: t("putaway.override", { proposed: proposal.code }) });
          if ((registry.bundle.siteConfig?.putAway?.enforcement ?? "free") === "reason") {
            // The site wants a reason before the move is recorded (D4-4 override-with-reason).
            setOverride({ state, destination: { id: code.id, code: code.code } });
            return "accepted";
          }
        }
        if (effects.some((e) => e.kind === "completed")) void complete(state, { id: code.id, code: code.code });
        return "accepted";
      }
      feedback("rejected");
      return "rejected";
    },
    [registry, resolveUnit, askProposal, complete, proposal],
  );

  const onCameraCode = useCallback(async (data: string) => {
    const st = await handleRaw(data);
    if (st === "accepted") setCamera(false);
    return st;
  }, [handleRaw]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el || camera || override) return;
    focusScan(el);
    return attachWedge(el, { onScan: (d) => void handleRaw(d) });
  }, [handleRaw, camera, override]);
  useEffect(() => {
    if (!camera) focusScan(inputRef.current);
  }, [step?.id, camera]);

  if (camera) return <CameraView onCode={onCameraCode} onClose={() => setCamera(false)} />;

  const overrideText = overrideReason === "other" ? overrideOther.trim() : overrideReason ?? "";
  const confirmOverride = () => {
    if (!override || !overrideText) return;
    const o = override;
    setOverride(null);
    void complete(o.state, o.destination, overrideText);
  };
  const cancelOverride = () => {
    // Back to the destination step: the scanned place is forgotten, the proposal stands.
    setOverride(null);
    setOverrideReason(null);
    setBanner(null);
    const { state } = applyInput(FLOW, startRun(FLOW).state, { kind: "scan", parsed: unit!.elements, resolved: { as: "container", containerId: unit!.container.container_id, containerKind: unit!.container.kind === "logistic-unit" ? "logistic-unit" : "trade-item", identityKey: unit!.container.identity_key } });
    const { state: s2 } = applyInput(FLOW, state, { kind: "answer", value: proposal && proposal !== "none" ? { locationId: proposal.locationId, code: proposal.code } : { locationId: null, code: null } });
    runRef.current = s2;
    setRun(s2);
  };

  const isUnitStep = step?.id === "unit";
  const cells: LabelCell[] = unit ? unitCells(unit) : [];
  const unitName = unit ? (unit.container.product_id ? registry.bundle.products.find((p) => p.id === unit.container.product_id)?.name ?? t("label.unknownProduct") : unit.container.kind === "logistic-unit" ? t("unit.pallet") : t("label.unknownProduct")) : "";

  return (
    <div className="screen" onClick={() => { unlockAudio(); focusScan(inputRef.current); }}>
      <Strip left={<b>{t("menu.putaway")}</b>} middle={unit?.container.sscc ? `${t("unit.pallet")} ${shortSscc(unit.container.sscc)}` : waiting ? t("putaway.waiting", { n: waiting.length }) : undefined} {...net} />
      <div className="task">
        <div className="task-main">
          {isUnitStep ? (
            <Prompt step={t("putaway.step1")} title={t("putaway.title1")} />
          ) : (
            <Prompt step={t("putaway.step2")} title={t("putaway.title2")} />
          )}
          {!isUnitStep && proposal && proposal !== "none" && (
            <div className="proposal">
              <div className="t">{t("putaway.proposed")}</div>
              {/* The place in 3D when the site's published layout knows it (owner 2026-11-02); the code and the 2D bay elevation otherwise. */}
              <GotoScene
                code={proposal.code}
                sub={proposal.reason === "nearest-empty-class" ? t("putaway.reason.class") : proposal.reason === "near-same-product" ? t("putaway.reason.sameProduct") : proposal.reason === "fixed-bin" ? t("putaway.reason.fixed") : t("putaway.reason.nearest")}
                fallback={
                  <>
                    <div className="loc">{proposal.code}</div>
                    <div className="hint">{proposal.path !== proposal.code ? `${proposal.path} · ` : ""}{proposal.reason === "nearest-empty-class" ? t("putaway.reason.class") : proposal.reason === "near-same-product" ? t("putaway.reason.sameProduct") : proposal.reason === "fixed-bin" ? t("putaway.reason.fixed") : t("putaway.reason.nearest")}</div>
                    <div className="mini-map"><BayElevation locations={registry.bundle.locations} binId={proposal.locationId} compact /></div>
                  </>
                }
              />
            </div>
          )}
          {!isUnitStep && proposal === "none" && <Banner kind="warn" label={t("banner.acceptedDeviation")} message={t("putaway.noProposal")} />}
          {!isUnitStep && proposal === null && <p className="hint">{t("common.loading")}</p>}
          {override ? (
            <>
              <Banner kind="warn" label={t("putaway.overrideAsk", { code: override.destination.code })} message={t("putaway.overrideWhy", { proposed: proposal && proposal !== "none" ? proposal.code : "" })} />
              <Chips options={reasonsForOverride(registry.bundle.siteConfig ?? null)} value={overrideReason} onChange={setOverrideReason} />
              {overrideReason === "other" && <input className="input" value={overrideOther} onChange={(e) => setOverrideOther(e.target.value)} placeholder={t("close.otherPlaceholder")} />}
            </>
          ) : (
            <>
              <ScanBar inputRef={inputRef} placeholder={t("scan.ready")} onCamera={() => { unlockAudio(); setCamera(true); }} />
              {banner && <Banner {...banner} />}
            </>
          )}
          {isUnitStep && !unit && waiting && waiting.length > 0 && (
            <div className="list">
              {waiting.slice(0, 12).map((w) => (
                <ListItem key={w.containerId} title={w.sscc ? `${t("unit.pallet")} ${shortSscc(w.sscc)}` : w.lotNumber ?? w.gtin ?? ""} desc={w.children > 0 ? t("rows.kolli", { n: w.children }) : undefined} onClick={() => { if (w.sscc) void handleRaw(`(00)${w.sscc}`); }} />
              ))}
            </div>
          )}
          {unit ? (
            <>
              <LabelStrip title={unit.container.kind === "logistic-unit" && !unit.container.product_id ? t("unit.pallet") : `${unit.container.kind === "logistic-unit" ? t("unit.pallet") : t("unit.kolli")} · ${unitName}`} sub={unit.container.kind === "logistic-unit" ? t("label.pallet") : t("label.last")} cells={cells} />
              {unit.container.kind === "logistic-unit" && contentRows.length > 0 && <Rows compact title={t("putaway.contents", { n: contents?.length ?? 0 })} rows={contentRows} />}
            </>
          ) : (
            <LabelStrip empty={waiting === null ? t("common.loading") : waiting.length === 0 ? t("putaway.waitingNone") : t("putaway.waiting", { n: waiting.length })} />
          )}
          {busy && <p className="hint">{t("common.loading")}</p>}
        </div>
        <aside className="task-side">
        </aside>
      </div>
      <Actions>
        {override ? (
          <>
            <Btn kind="secondary" onClick={cancelOverride}>{t("common.cancel")}</Btn>
            <Btn kind="primary" disabled={!overrideText} onClick={confirmOverride}>{t("putaway.overrideConfirm")}</Btn>
          </>
        ) : (
          <>
            <Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn>
            {unit?.container.kind === "logistic-unit" && <Btn kind="secondary" onClick={() => void Api.printJob({ kind: "pallet-label", containerId: unit.container.container_id }).then((job) => { feedback("accepted"); setBanner({ kind: "ok", label: t("move.printed"), message: t("move.printedMsg", { printer: job.printerName }) }); }).catch((e: unknown) => { feedback("rejected"); const msg = e instanceof Error ? e.message : String(e); setBanner({ kind: "bad", label: t("banner.rejected"), message: /no_printer|no active/i.test(msg) ? t("move.noPrinter") : msg }); })}>{t("move.printLabel")}</Btn>}
            {!isUnitStep && <Btn kind="secondary" onClick={() => { setProposal("none"); }}>{t("putaway.other")}</Btn>}
          </>
        )}
      </Actions>
    </div>
  );
}

function unitCells(u: Unit): LabelCell[] {
  const c = u.container;
  const cells: LabelCell[] = [];
  if (c.sscc) cells.push({ title: "SSCC", value: c.sscc, wide: true });
  else if (c.gtin) cells.push({ title: "GTIN", value: c.gtin, wide: true });
  if (c.kind === "logistic-unit") cells.push({ title: "Count", value: <>{c.children} <small>{t("unit.kolli").toLowerCase()}</small></> });
  else {
    if (c.kind !== "loose") cells.push({ title: "Batch/Lot", value: c.lot_number ?? <small>{t("label.missing")}</small> });
    cells.push({ title: "Content", value: fmtQty(c.quantity, c.uom) });
  }
  return cells;
}

function describeUnit(u: Unit): string {
  const c = u.container;
  return c.sscc ? `${t("unit.pallet")} ${c.sscc}` : `${c.gtin ?? ""}${c.lot_number && c.kind !== "loose" ? ` · ${c.lot_number}` : ""}`;
}
